/* ============================================================================
   Global light/dark theme state.

   Before this module, dark mode was local `data()` on LayoutConsoleV2 and the
   `.dark-mode` class sat on that layout's <v-app>. That meant the auth/login
   pages and the developer console could never go dark, and detached overlay
   content (v-menu / v-dialog / v-tooltip, which Vuetify moves out of the
   component tree) only inherited it by accident.

   The class now lives on <html>, so every rule already written as
   `.dark-mode .x` — ~2,300 of them across the app — applies everywhere,
   including detached content. `$vuetify.theme.dark` is kept in step so
   Vuetify's own `theme--dark` styling and the `--tbl-*` / `--app-*` token
   layers switch at the same time.
   ============================================================================ */
import Vue from "vue";

// Same key LayoutConsoleV2 has always written, so anyone who flipped the old
// (hidden) toggle keeps their preference.
export const THEME_STORAGE_KEY = "darkMode";

const DARK_CLASS = "dark-mode";

// Kills every CSS transition while the palette changes — see the matching
// block at the top of theme-dark.css.
const SWITCHING_CLASS = "theme-switching";

/**
 * The stored preference, or false. Light is the default: the OS
 * `prefers-color-scheme` is deliberately NOT consulted, so a first-time visit
 * always lands on the light theme.
 */
export function isDarkPreferred() {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "true";
  } catch (e) {
    // Safari private mode throws on localStorage access.
    return false;
  }
}

// Reactive so components can `import { themeState }` and bind to it directly.
export const themeState = Vue.observable({ dark: isDarkPreferred() });

// Vuetify's theme service, captured in initTheme().
let vuetifyTheme = null;

function paint(dark) {
  const root = document.documentElement;
  root.classList.toggle(DARK_CLASS, dark);
  // Bootstrap 5.3 reads this attribute for its own dark palette.
  root.setAttribute("data-bs-theme", dark ? "dark" : "light");
  // Native widgets: scrollbars, form controls, autofill, date pickers.
  root.style.colorScheme = dark ? "dark" : "light";
}

/**
 * Run a palette change with all CSS transitions suppressed, so the switch is
 * instant instead of ~1,000 elements each easing to their new colour.
 *
 * The suppression has to outlive the synchronous swap. Vuetify components carry
 * `theme--light` / `theme--dark` as REACTIVE classes bound to
 * `$vuetify.theme.dark`, so their colours only change once Vue re-renders, one
 * flush after the `.dark-mode` class lands on <html>. Dropping the guard before
 * that would let the second wave fade.
 *
 * The forced reflow is the load-bearing part: a transition starts when a
 * property's computed value differs between two style recalcs. Reading
 * offsetHeight forces a recalc while transitions are still off, which commits
 * every new colour as the baseline; removing the class afterwards then has
 * nothing left to animate. Without it, both the palette change and the class
 * removal would land in the same recalc and every transition would fire.
 */
function withoutTransitions(mutate) {
  const root = document.documentElement;
  root.classList.add(SWITCHING_CLASS);
  mutate();
  Vue.nextTick(() => {
    void root.offsetHeight;
    root.classList.remove(SWITCHING_CLASS);
  });
}

/** Wire the theme to a Vuetify instance and paint the current preference. */
export function initTheme(vuetify) {
  vuetifyTheme = vuetify.framework.theme;
  // vuetify.js already seeds `theme.dark` from the same preference. Assigning
  // it again before Vuetify's own init() would force an early applyTheme() and
  // leave a stray <style> element behind, so only write on a real mismatch.
  if (vuetifyTheme.dark !== themeState.dark) vuetifyTheme.dark = themeState.dark;
  paint(themeState.dark);
}

export function setDark(dark) {
  withoutTransitions(() => {
    themeState.dark = !!dark;
    if (vuetifyTheme) vuetifyTheme.dark = themeState.dark;
    paint(themeState.dark);
  });
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, String(themeState.dark));
  } catch (e) {
    /* preference just won't survive the session */
  }
}

export function toggleDark() {
  setDark(!themeState.dark);
}

export default { themeState, initTheme, setDark, toggleDark, isDarkPreferred };
